import { useEffect, useId, useMemo, type HTMLAttributes, type ReactNode } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { STATUS_LABEL, type TaskStatus, type TaskWithFlags } from "@desk/contracts";
import type { BoardDnd } from "@/concepts/types";
import { useConcept } from "@/features/concept-provider";
import { locationLabel, useHierarchy } from "@/data/hooks";
import { useOverlay } from "@/data/outbox";
import { dedupe } from "@/lib/filters";
import { openTask, ui, useUi } from "@/features/ui-state";
import { TaskMenu } from "@/features/task/TaskMenu";
import { taskRegistry } from "@/features/list/registry";
import type { DragItem, DropData } from "@/features/dnd/DeskDnd";

const OPEN_COLUMNS: TaskStatus[] = ["backlog", "todo", "in_progress"];

/**
 * Board drag hooks for one board. Cards drop on a status column, or on a sidebar area, space,
 * project, or Inbox to move there; the shared DeskDndProvider routes the drop.
 */
function boardDnd(boardId: string, byId: (id: string) => TaskWithFlags | undefined): BoardDnd {
  return {
    useColumn(status) {
      const data: DropData = { type: "column", listId: boardId, status };
      const d = useDroppable({ id: `${boardId}:col:${status}`, data });
      return { setNodeRef: d.setNodeRef, isOver: d.isOver };
    },
    useCard(taskId) {
      const task = byId(taskId);
      const data: DragItem | undefined = task && { type: "task", listId: boardId, task };
      const d = useDraggable({ id: `${boardId}:${taskId}`, data });
      return {
        setNodeRef: d.setNodeRef,
        attributes: d.attributes as unknown as HTMLAttributes<HTMLElement>,
        listeners: d.listeners as unknown as HTMLAttributes<HTMLElement> | undefined,
        // The lifted copy is drawn by the drag overlay; the card stays put as a placeholder.
        style: { opacity: d.isDragging ? 0.4 : undefined, position: "relative" },
        isDragging: d.isDragging,
      };
    },
  };
}

export function BoardView({
  tasks,
  showClosed,
  location,
  empty,
}: {
  tasks: TaskWithFlags[];
  showClosed: boolean;
  location?: (t: TaskWithFlags) => string | null;
  empty?: ReactNode;
}) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const overlay = useOverlay();
  const selectedId = useUi((s) => s.selectedTaskId);
  // A list's multi-selection carries over to its board; show it, since dragging a selected card moves them all.
  const selectedIds = useUi((s) => s.selectedIds);
  const boardId = useId();

  const statuses = showClosed ? [...OPEN_COLUMNS, "done", "cancelled"] as TaskStatus[] : OPEN_COLUMNS;
  const unique = useMemo(() => dedupe(tasks), [tasks]);
  const dnd = useMemo(() => boardDnd(boardId, (id) => unique.find((t) => t.id === id)), [boardId, unique]);
  const columns = statuses.map((status) => ({ status, label: STATUS_LABEL[status], tasks: unique.filter((t) => t.status === status) }));

  useEffect(() => {
    unique.forEach((t) => taskRegistry.set(t.id, t));
    return () => unique.forEach((t) => taskRegistry.delete(t.id));
  }, [unique]);

  const locate = location ?? ((t: TaskWithFlags) => locationLabel(t, h));

  return (
    <>
      <concept.Board
        columns={columns}
        dnd={dnd}
        empty={empty}
        card={(task) => ({
          task,
          location: locate(task),
          sync: overlay.marks.get(task.id) ?? null,
          today: h.today,
          timezone: h.timezone,
          selected: selectedId === task.id || selectedIds.includes(task.id),
          onOpen: () => {
            ui.set({ selectedTaskId: task.id, selectedIds: [], selectionAnchor: task.id });
            openTask(task.id, concept.detailMode === "inline" ? "overlay" : "list");
          },
          menu: <TaskMenu task={task} statusFirst />,
        })}
      />
      {/* Inline expansion doesn't fit a board: TaskDetailHost presents it as a sheet ("overlay"). */}
    </>
  );
}
